import React, { useCallback, useRef, useState } from 'react';
import { merchantApi } from '../../api/config';

export interface AttachmentItem {
  file_url: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
}

interface Props {
  value: AttachmentItem[];
  onChange: (next: AttachmentItem[]) => void;
  maxFiles?: number;
  maxSizeMB?: number;
  endpoint?: string; // ใช้ /tickets/upload เป็น default ใช้ทั้ง merchant + admin (admin reuse endpoint เดียวกัน แต่จะเสริม /admin/tickets ในอนาคต)
}

function formatBytes(bytes: number) {
  if (!bytes) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes; let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 10 || i === 0 ? 0 : 1)}${units[i]}`;
}

function fileIcon(mime: string) {
  if (mime.startsWith('image/')) return '🖼';
  if (mime === 'application/pdf') return '📄';
  if (mime.includes('zip')) return '🗜';
  if (mime.includes('sheet') || mime.includes('excel') || mime.includes('csv')) return '📊';
  return '📎';
}

const TicketAttachmentUploader: React.FC<Props> = ({
  value, onChange, maxFiles = 5, maxSizeMB = 10, endpoint = '/tickets/upload',
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onPick = () => inputRef.current?.click();

  const uploadOne = useCallback(async (file: File) => {
    const form = new FormData();
    form.append('file', file);
    const res = await merchantApi.post(endpoint, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data?.data as AttachmentItem | undefined;
  }, [endpoint]);

  const handleFiles = useCallback(async (files: FileList | File[]) => {
    setError(null);
    const arr = Array.from(files);
    if (value.length + arr.length > maxFiles) {
      setError(`อัปโหลดได้สูงสุด ${maxFiles} ไฟล์`);
      return;
    }
    for (const f of arr) {
      if (f.size > maxSizeMB * 1024 * 1024) {
        setError(`${f.name}: ขนาดเกิน ${maxSizeMB}MB`);
        return;
      }
    }

    setUploading(true);
    try {
      const next = [...value];
      for (const f of arr) {
        try {
          const item = await uploadOne(f);
          if (item) next.push(item);
        } catch (e: any) {
          const msg = e?.response?.data?.message || e?.message || 'อัปโหลดไม่สำเร็จ';
          setError(`${f.name}: ${msg}`);
        }
      }
      onChange(next);
    } finally {
      setUploading(false);
    }
  }, [value, onChange, maxFiles, maxSizeMB, uploadOne]);

  const remove = (idx: number) => {
    const next = value.slice();
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => {
          e.preventDefault();
          setDragOver(false);
          if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
        }}
        className={`border-2 border-dashed rounded-lg p-4 text-center transition cursor-pointer ${
          dragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
        }`}
        onClick={onPick}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          accept="image/*,application/pdf,.csv,.txt,.xls,.xlsx,.zip"
          onChange={e => { if (e.target.files) handleFiles(e.target.files); e.target.value = ''; }}
        />
        <div className="text-sm text-slate-600">
          {uploading ? 'กำลังอัปโหลด...' : 'คลิกหรือลากไฟล์มาวางที่นี่'}
        </div>
        <div className="text-[11px] text-slate-400 mt-1">
          รองรับ image / pdf / csv / xlsx / zip — สูงสุด {maxSizeMB}MB · {maxFiles} ไฟล์
        </div>
      </div>

      {error && <div className="text-xs text-rose-600">{error}</div>}

      {value.length > 0 && (
        <ul className="space-y-1">
          {value.map((a, idx) => (
            <li key={`${a.file_url}-${idx}`} className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg">
              <span className="text-lg leading-none">{fileIcon(a.mime_type)}</span>
              <a
                href={a.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 text-sm text-slate-700 hover:text-blue-600 truncate"
                title={a.file_name}
              >
                {a.file_name}
              </a>
              <span className="text-[11px] text-slate-400">{formatBytes(a.size_bytes)}</span>
              <button
                type="button"
                onClick={() => remove(idx)}
                className="text-slate-400 hover:text-rose-600 text-sm"
                title="ลบ"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TicketAttachmentUploader;
